import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

/**
 * Stimulus controller for city auto-suggestions in lead forms.
 *
 * Usage: add `data-controller="locations--city"` on the container and define a
 * `data-locations--city-target="cityInput"` on the city input element.
 */
export default class extends Controller {
  static targets = ["cityInput", "countryInput", "stateInput"]

  connect() {
    if (!this.hasCityInputTarget) return

    this.countryEl = document.getElementById("lead-country-field")
    this.stateEl = document.getElementById("lead-state-field")

    this.country = this.countryEl?.value || ""
    this.state = this.stateEl?.value || ""

    // Initialize Selectize on the input
    this.citySelectize = $(this.cityInputTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
      persist: false,
      create: false,
      maxItems: 1, // Only allow single selection
      load: (query, callback) => {
        if (!this.country) return callback()
        const trimmed = (query || "").trim()
        if (trimmed.length < 2) return callback()

        const params = new URLSearchParams({ country: this.country })
        if (this.state) params.append("state", this.state)
        params.append("q", trimmed)

        fetch(`/cities?${params.toString()}`, { headers: { Accept: "application/json" } })
          .then((r) => r.json())
          .then((cities) => callback(cities))
          .catch(() => callback())
      },
      // Remove the default dropdown arrow since it's single select
      plugins: [],
    })[0].selectize

    this.updateEnabled()
    this.#initializeCountrySelectize()
    this.#initializeStateSelectize()
  }

  disconnect() {
    this.citySelectize?.destroy()
  }

  countryChanged(event) {
    this.country = event.target.value || ""
    this.state = this.stateEl?.value || ""
    this.updateEnabled()

    if (this.citySelectize) {
      this.citySelectize.clear()
      this.citySelectize.clearOptions()
    }
  }

  stateChanged(event) {
    this.state = event.target.value || ""

    if (this.citySelectize) {
      this.citySelectize.clear()
      this.citySelectize.clearOptions()
    }
  }

  #initializeCountrySelectize() {
    if (!this.hasCountryInputTarget) return

    this.countrySelectize = $(this.countryInputTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
      onChange: () => {
        // Ensure other controllers (e.g., show-hide) listening to native change
        // events are notified when Selectize changes the value
        this.countryInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      },
    })
  }

  #initializeStateSelectize() {
    if (!this.hasStateInputTarget) return

    this.stateSelectize = $(this.stateInputTarget).selectize({
      valueField: "value",
      labelField: "label",
      searchField: "label",
    })
  }

  updateEnabled() {
    if (!this.citySelectize) return

    const shouldEnable = Boolean(this.country)
    if (shouldEnable) {
      this.citySelectize.enable()
      this.updatePlaceholder(i18n.t("js.back.leads.form.select_a_city"))
    } else {
      this.citySelectize.disable()
      this.citySelectize.clear()
      this.updatePlaceholder(i18n.t("js.back.leads.form.select_a_country_first"))
    }
  }

  updatePlaceholder(placeholder) {
    if (!this.citySelectize) return
    this.citySelectize.settings.placeholder = placeholder
    this.citySelectize.updatePlaceholder()
  }
}


